import React, { memo, useCallback } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import Popup from '@atlaskit/popup';

import type { DatePickerValue, FuzzyDatePickerProps } from '../types';

import { DateRange } from './date-range';
import DateRangePickerContent from './date-range-picker-content';

export const DateRangePicker: React.MemoExoticComponent<
	({
		value,
		onChange,
		placeholder,
		onClose,
		withTimeScale,
		appearance,
		disabled,
		minDate,
		maxDate,
		granularities,
		showPlaceholderWithSubtleText,
	}: FuzzyDatePickerProps) => React.JSX.Element
> = memo(
	({
		value,
		onChange,
		placeholder,
		onClose,
		withTimeScale = true,
		appearance = 'default',
		disabled = false,
		minDate,
		maxDate,
		granularities,
		showPlaceholderWithSubtleText,
	}: FuzzyDatePickerProps): React.JSX.Element => {
		const [isOpen, setIsOpen] = React.useState(false);
		const [currentValue, setCurentValue] = React.useState<DatePickerValue | undefined>(value);

		React.useEffect(() => {
			fg('projects_ai_updates') && setCurentValue(value);
		}, [value]);

		const togglePopup = useCallback(() => {
			if (!disabled) {
				if (isOpen) {
					onClose?.();
				}
				setIsOpen(!isOpen);
			}
		}, [isOpen, onClose, disabled]);

		const closePopup = () => {
			setIsOpen(false);
			onClose?.();
		};

		const onDateChange = useCallback(
			(newValue?: DatePickerValue, confidence?: string) => {
				setCurentValue(newValue);
				onChange(newValue, confidence);
				setIsOpen(false);
				onClose?.();
			},
			[onChange, onClose],
		);

		const onClearDate = useCallback(() => {
			onDateChange();
		}, [onDateChange]);

		return (
			<Popup
				zIndex={610}
				placement="bottom-start"
				content={() => (
					<DateRangePickerContent
						value={value}
						onChange={onDateChange}
						withTimeScale={withTimeScale}
						minDate={minDate}
						maxDate={maxDate}
						granularities={granularities}
					/>
				)}
				isOpen={isOpen && !disabled}
				onClose={closePopup}
				shouldUseCaptureOnOutsideClick
				trigger={(triggerProps) => (
					<DateRange
						onClick={disabled ? undefined : togglePopup}
						value={currentValue}
						placeholder={placeholder}
						triggerProps={triggerProps}
						onClearDate={onClearDate}
						withTimeScale={withTimeScale}
						appearance={appearance}
						disabled={disabled}
						showPlaceholderWithSubtleText={showPlaceholderWithSubtleText}
					/>
				)}
				shouldRenderToParent={fg('should-render-to-parent-should-be-true-townsquare')}
			/>
		);
	},
);
